import { useApiActivity } from '@/hooks/useApiActivity';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Activity } from 'lucide-react';
import { format } from 'date-fns';

export function ApiActivitySettings() {
  const { data: activity, isLoading, isError, error } = useApiActivity(100);

  const getStatusVariant = (statusCode: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (statusCode >= 200 && statusCode < 300) {
      return 'secondary';
    }
    if (statusCode >= 400 && statusCode < 500) {
      return 'outline';
    }
    if (statusCode >= 500) {
      return 'destructive';
    }
    return 'default';
  };

  const getMethodVariant = (method: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'secondary';
      case 'POST':
        return 'default';
      case 'PUT':
      case 'PATCH':
        return 'outline';
      case 'DELETE':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-destructive">
        <AlertCircle className="h-8 w-8 mb-2" />
        <p>Error loading API activity:</p>
        <p className="text-sm">{error?.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">API Activity</h2>
        <p className="text-muted-foreground">
          Monitor recent API requests to your workspace
        </p>
      </div>

      {activity && activity.length > 0 ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Time</TableHead>
                <TableHead className="w-[80px]">Method</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead className="w-[80px]">Status</TableHead>
                <TableHead className="w-[100px] text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activity.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(log.created_at), 'MMM d, HH:mm:ss')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getMethodVariant(log.method)} className="font-mono text-xs">
                      {log.method}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {log.endpoint}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(log.status_code)}>
                      {log.status_code}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {formatDuration(log.duration_ms)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/50">
          <Activity className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="font-medium mb-1">No API activity yet</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            API requests made using your API keys will appear here.
            Create an API key and make your first request to see activity.
          </p>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        Showing the last {activity?.length || 0} API requests. Activity is refreshed every 30 seconds.
      </div>
    </div>
  );
}
